import { useEffect, useState } from "react"

import { Col, Form, Input, Row, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"

import Icon from "../../Icon"
import utils from "../../Utils/utils"
import ButtonComponent from "../Button"
import InputComponent from "../Input"
import PasswordValidator from "../PasswordValidator"

const { Text, Link: AntLink } = Typography

const errorsMapByCode = {
  email_already_exists:
    process.env.REACT_APP_TYPE === "partner" ? (
      <>
        Referral account already created. Kindly try another one or{" "}
        <NavLink style={{ fontSize: 12 }} to="/login">
          Sign In
        </NavLink>
      </>
    ) : (
      <>
        This email already exists. Kindly try another one or{" "}
        <NavLink style={{ fontSize: 12 }} to="/login">
          Sign In
        </NavLink>
      </>
    )
}

function SignUpEmailForm({
  error,
  initialValues,
  inviteInfo,
  acceptPolicyAgreement,
  setAcceptPolicyAgreement,
  hideEmail = false,
  ...props
}) {
  const [errorStr, setErrorStr] = useState(null)
  const [passwordError, setPasswordError] = useState(null)
  const [errorLink, setErrorLink] = useState(null)
  const [errorCode, setErrorCode] = useState(null)
  const [value, setValue] = useState(null)

  const [isVisibleEmail, setIsVisibleEmail] = useState(false)
  const [isVisibleBtn, setIsVisibleBtn] = useState(false)

  const [form] = Form.useForm()
  const { t } = useTranslation()

  useEffect(() => {
    if (error) {
      if (typeof error === "string") {
        if (error.length > 100) {
          setErrorStr(t("Server response error"))
        } else {
          setErrorStr(error)
        }
      } else {
        setErrorStr(error?.detail || error?.message?.detail || null)
        setErrorLink(error?.link ?? null)
        setErrorCode(error?.code ?? null)
      }
    }
  }, [error, t])

  useEffect(() => {
    if (inviteInfo) form.setFieldValue("email", inviteInfo?.["invited_email"])
  }, [inviteInfo])

  const onFinish = (values) => {
    if (!acceptPolicyAgreement) return
    const data = { ...values }
    delete data.accept
    delete data.confirm
    props.onSubmit(values)
  }

  const onFinishFailed = ({ errorFields }) => {
    const firstError = errorFields && errorFields[0]
    if (firstError?.name?.[0] === "confirm") {
      setPasswordError(firstError?.errors?.[0])
      setErrorStr(null)
    } else {
      setErrorStr(firstError?.errors?.[0])
      setPasswordError(null)
    }
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
    setErrorStr(null)
    setPasswordError(null)
    setErrorLink(null)
    setErrorCode(null)
  }

  function handleChange(e) {
    const { name, value } = e.target

    if (name === "last_name") {
      if (value) {
        if (!isVisibleEmail) setIsVisibleEmail(true)
      } else {
        if (isVisibleEmail) setIsVisibleEmail(false)
      }
    }
    if (name === "password") {
      setValue(value)
    }

    const emailValue = name === "email" ? value : form.getFieldValue("email")
    const isEmailValid = hideEmail ? true : utils.validateEmail(emailValue)

    if (
      isEmailValid &&
      form.getFieldValue("password")?.length >= 8 &&
      form.getFieldValue("first_name") &&
      form.getFieldValue("last_name")
    ) {
      setIsVisibleBtn(true)
    } else {
      setIsVisibleBtn(false)
    }
  }

  const errorEl = errorsMapByCode[errorCode] || errorStr

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger="onSubmit"
      className="login-form"
      layout="vertical"
      requiredMark={false}
      {...(initialValues && { initialValues })}
    >
      {props.children}

      <Form.Item noStyle shouldUpdate={true}>
        {() => (
          <div
            className={cls("error-text", {
              "error-text_empty": !errorEl || passwordError
            })}
          >
            {errorEl && (
              <>
                <Icon type={"alert_triangle_error"} role={"icon"} />
                {errorEl}
                {errorLink && !errorCode && (
                  <AntLink
                    style={{ fontSize: "12px" }}
                    href={errorLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {" " + errorLink}
                  </AntLink>
                )}
              </>
            )}
          </div>
        )}
      </Form.Item>

      <Row gutter={16}>
        <Col span={12} style={{ paddingRight: 6 }}>
          <Form.Item
            name="first_name"
            rules={[
              { required: true, message: t("Kindly insert your full name") },
              {
                type: "string",
                message: t("You can only use letters to insert your name"),
                pattern: /^[a-zA-Z ]+$/,
                transform(value) {
                  if (value) return value.trim()
                }
              }
            ]}
          >
            <InputComponent
              placeholder={t("First Name")}
              autoComplete="given-name"
              onChange={(e) => {
                handleChange(e)
                handleClearError(e, "first_name")
              }}
            />
          </Form.Item>
        </Col>
        <Col span={12} style={{ paddingLeft: 6 }}>
          <Form.Item
            name="last_name"
            rules={[
              { required: true, message: t("Kindly insert your full name") },
              {
                type: "string",
                message: t("You can only use letters to insert your name"),
                pattern: /^[a-zA-Z ]+$/,
                transform(value) {
                  if (value) return value.trim()
                }
              }
            ]}
          >
            <InputComponent
              placeholder={t("Last Name")}
              autoComplete="family-name"
              name="last_name"
              onChange={(e) => {
                handleChange(e)
                handleClearError(e, "last_name")
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      {!hideEmail && (
        <Form.Item
          name="email"
          validateStatus={errorEl ? "error" : null}
          rules={[
            { required: true, message: t("Please insert your Email") },
            { type: "email", message: t("The email you entered is incorrect") }
          ]}
        >
          <InputComponent
            placeholder={"Email"}
            name="email"
            type="email"
            autoComplete="email"
            disabled={!!inviteInfo}
            onChange={(e) => {
              handleChange(e)
              handleClearError(e, "email")
            }}
          />
        </Form.Item>
      )}

      <Form.Item
        name="password"
        rules={[{ required: true, message: t("Please insert your password") }]}
      >
        <Input.Password
          placeholder={t("Password")}
          name={"password"}
          type={"password"}
          autoComplete="new-password"
          visibilityToggle
          onChange={(e) => {
            handleChange(e)
            handleClearError(e, "password")
          }}
        />
      </Form.Item>

      {!errorEl || passwordError ? (
        <PasswordValidator value={value} error={passwordError} isLogin={true} />
      ) : null}

      <Form.Item
        name="confirm"
        rules={[
          {
            required: true,
            message: t("Please confirm your password!")
          },
          ({ getFieldValue }) => ({
            validator(rule, value) {
              if (!value || !getFieldValue("password") || getFieldValue("password") === value) {
                if (value?.length < 8) {
                  return Promise.reject(t("Must be at least 8 characters"))
                } else {
                  return Promise.resolve()
                }
              } else {
                return Promise.reject(t("Passwords do not match"))
              }
            }
          })
        ]}
      >
        <Input.Password
          name="confirm"
          placeholder={t("Confirm password")}
          autoComplete="dontshow"
          visibilityToggle
          onChange={(e) => {
            handleChange(e)
            handleClearError(e, "confirm")
          }}
        />
      </Form.Item>

      {/*<Flex gap={8} align="flex-start">
        <Checkbox
          checked={acceptPolicyAgreement}
          onChange={() => setAcceptPolicyAgreement(prev => !prev)}
        />
        <Text strong>
          {t('I accept the ')}
          <Typography.Link href="https://www.chargeback.io/terms-and-conditions" target="_blank">{t('Terms & Conditions')}</Typography.Link>
          {` ${t('and')} `}
          <Typography.Link href="https://www.chargeback.io/privacy-policy" target="_blank">{t('Privacy Policy')}</Typography.Link>
        </Text>
      </Flex>*/}

      <ButtonComponent
        type="primary"
        size="large"
        htmlType="submit"
        className="btn-primary_animation"
        disabled={!isVisibleBtn || !acceptPolicyAgreement}
        block
      >
        {t("Create an account")}
      </ButtonComponent>
    </Form>
  )
}

export default SignUpEmailForm
